import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { translations, Locale } from '@/translations';
import { trackEvent } from '@/utils/analytics';

interface LocalizationContextType {
  /** Current locale (e.g. 'en', 'ar') */
  locale: Locale;
  /** Change the active locale. Persists to localStorage and updates document dir/lang. */
  setLocale: (locale: Locale) => void;
  /**
   * Translate a dot-separated key, with optional interpolation.
   * @example t('footer.copyright', { year: '2026' }) => "All rights reserved to WTD Medical © 2026"
   * @example t('common.loading') => "Loading..."
   */
  t: (key: string, params?: Record<string, string | number>) => string;
  /** Shorthand: current locale is RTL */
  isRTL: boolean;
  /** The language code used by the current locale (e.g. 'en', 'ar') – same as locale */
  language: Locale;
  /** Alias for setLocale – matches the old LanguageContext API */
  setLanguage: (lang: Locale) => void;
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

export const LocalizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const savedLocale = localStorage.getItem('locale') as Locale;
    return savedLocale && ['en', 'ar'].includes(savedLocale) ? savedLocale : 'en';
  });

  const prevLocaleRef = useRef(locale);

  const setLocale = useCallback((newLocale: Locale) => {
    const previousLocale = prevLocaleRef.current;
    setLocaleState(newLocale);
    localStorage.setItem('locale', newLocale);
    prevLocaleRef.current = newLocale;

    // Track language change (GA4/GTM + Mixpanel)
    if (previousLocale !== newLocale) {
      trackEvent('language_changed', {
        from_language: previousLocale,
        to_language: newLocale,
      });
    }
  }, []);

  // Update document direction for RTL languages
  useEffect(() => {
    if (locale === 'ar') {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = 'ar';
    } else {
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = 'en';
    }
  }, [locale]);

  const isRTL = locale === 'ar';

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const keys = key.split('.');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let value: any = translations[locale];

      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          // Fallback to English if translation is missing
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let fallback: any = translations.en;
          for (const fk of keys) {
            if (fallback && typeof fallback === 'object' && fk in fallback) {
              fallback = fallback[fk];
            } else {
              return key; // Return the key if no translation found
            }
          }
          value = typeof fallback === 'string' ? fallback : key;
          break;
        }
      }

      let result = typeof value === 'string' ? value : key;

      // Handle interpolation: replace {{key}} with params
      if (params && typeof result === 'string') {
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          result = result.replace(
            new RegExp(`\\{\\{${paramKey}\\}\\}`, 'g'),
            String(paramValue)
          );
        });
      }

      return result;
    },
    [locale]
  );

  const contextValue = useMemo(
    () => ({
      locale,
      setLocale,
      t,
      isRTL,
      language: locale,
      setLanguage: setLocale,
    }),
    [locale, setLocale, t, isRTL]
  );

  return (
    <LocalizationContext.Provider value={contextValue}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
};
